import { UploadBundleSummary } from "./UploadBundleSummary";
import {
  Checkbox,
  Field,
  IconButton,
  SingleSelectDropdown,
  Text,
  TextInput,
} from "@carbon6/c6-frontend-design";
import { useId, type ReactNode } from "react";
import {
  displayedUploadLocation,
  uploadJobSupportsBandDiagnostics,
  type DataOrigin,
  type DatasetUploadLocation,
  type Embodiment,
  type ReservedKeyContract,
} from "@/api";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { DATA_ORIGIN_OPTIONS } from "@/components/dataset";
import { formatEmbodimentMeta, formatEmbodimentName } from "@/components/embodiment";
import { HatTextTooltip } from "@/components/tooltip";
import { ReservedLabelForm } from "@/page/datasetLabel/ReservedLabelForm";
import { FolderDropZone } from "@/components/dataset/FolderDropZone/FolderDropZone";
import { formatUploadBytes, formatUploadEta } from "@/page/datasetUpload/datasetUploadFormatters";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { isForeignLiveLease } from "@/upload";
import styles from "@/page/datasetUpload/datasetUpload.module.css";
import { FolderReadGuide } from "./FolderReadGuide";
import type {
  EmbodimentCatalogStatus,
  UploadDialogState,
  UploadWindowController,
} from "./UploadWindowController";

type UploadSetupFormProps = {
  controller: UploadWindowController;
  dialog: UploadDialogState;
  // Embodiment 드롭다운 후보. 컨트롤러가 창을 열 때 받은 카탈로그다.
  embodiments: readonly Embodiment[];
  // 카탈로그의 조회 상태. 받기 전과 실패를 빈 카탈로그와 구분해 드롭다운 문구로 알린다.
  embodimentCatalog: EmbodimentCatalogStatus;
  // 고를 수 있는 업로드 서버 위치(같은 카탈로그). 2개 미만이면 필드를 그리지 않는다.
  uploadLocations: readonly DatasetUploadLocation[];
  folderInputId: string;
  // 창을 닫는다. 설정 단계에는 잡이 없으므로 언제든 닫을 수 있다. onClose 콜백이 목록 새로고침
  // 등을 실행한다.
  onCancel: () => void;
  // 업로드를 시작하면 true. 모든 필드를 비활성·dim 처리하고 설정 액션(취소, 시작)을 숨긴다.
  disabled?: boolean;
  // 예약 라벨·embodiment 계약.
  dataset: {
    reservedContracts: readonly ReservedKeyContract[];
    reservedKeys: readonly string[];
    reservedRequiredKeys: readonly string[];
  };
};

/**
 * 업로드 설정 폼(files 모드). 상태와 검증은 UploadWindowController 가 소유한다. 이 컴포넌트는
 * 렌더만 한다.
 * 예약 라벨(환경, 출처)과 행동 주체 드롭다운은 균등 3열의 한 행에 둔다. embodiment 는 그 아래
 * 전체폭 행에 둔다. 행동 주체는 라벨이 아니라 Dataset 컬럼이다. 그래서 selections 에 넣지 않고
 * ReservedLabelForm 의 trailingCell 슬롯으로 전달한다. 헤더는 FullscreenWindow 가 소유한다.
 * disabled(업로드 시작 후)이면 모든 필드를 비활성·dim 하고 설정 액션을 감춘다. 정보를 바꿀 수 없다.
 */
export function UploadSetupForm({
  controller,
  dialog,
  embodiments,
  embodimentCatalog,
  uploadLocations,
  folderInputId,
  onCancel,
  disabled = false,
  dataset,
}: UploadSetupFormProps): ReactNode {
  const scan = dialog.form.scan;
  const embodiment = dialog.form.embodiment;
  const isResume = dialog.mode === "resume";
  const isRetry = dialog.mode === "retry";
  const nameLocked = isRetry;
  // 시작한 뒤에는 진행 중 여부(busy)와 무관하게 항상 비활성이다.
  const fieldsDisabled = disabled || dialog.busy;
  // 재업로드하는 잡의 정본 위치가 진단을 지원하지 않으면 선언을 켤 수 없다. 켜서 보내면 서버가 409 로
  // 거절한다. 새 업로드는 이 화면이 GCS 로만 만든다.
  const bandDiagnosticsSupported =
    dialog.targetJob == null || uploadJobSupportsBandDiagnostics(dialog.targetJob);
  const rejectedCount = scan?.rejected.length ?? 0;
  const submitText = "업로드 시작";
  // 읽지 못하는 폴더로는 시작을 허용하지 않는다. 확인이 끝나기 전에도 막는다. 컨트롤러도 같은
  // 판정을 다시 한다. 하지만 누를 수 있는 버튼을 두면 사용자는 원인을 안내가 아니라 실패로 알게 된다.
  const folderBlocked = dialog.checkingRead || dialog.readFailure !== null;
  // 재개 폴더 재선택 UI 는 시작 전(설정)에만 표시한다. 시작한 뒤에는 표준 필드를 비활성으로 표시한다.
  const showResumeBody = isResume && !disabled;
  const bandDiagnosticsToggleId = useId();
  // 위치 라디오 묶음의 name. 업로드 창이 여러 개 열려도 묶음이 서로 섞이지 않는다.
  const locationGroupName = useId();

  // 소유권 안내는 항상 드롭존과 함께 표시한다. resume 본문도 같은 자리에 그대로 둔다.
  const dropZone = (
    <>
      <FolderDropZone
        folderInputId={folderInputId}
        folderName={scan?.rootName ?? null}
        inputLabel="Dataset 폴더"
        caption={
          scan === null
            ? "폴더 내 모든 파일이 경로 그대로 업로드됩니다."
            : `파일 ${scan.files.length.toLocaleString()}개 · 총 ${formatUploadBytes(scan.totalBytes)} · 예상 ${formatUploadEta(scan.totalBytes)}`
        }
        scanning={dialog.scanning}
        dragActive={dialog.dragActive}
        disabled={fieldsDisabled}
        onFolderInputChange={controller.handleFolderInputChange}
        onClick={controller.handleDropZoneClick}
        onDragEnter={controller.handleDragEnter}
        onDragOver={controller.handleDragOver}
        onDragLeave={controller.handleDragLeave}
        onDrop={controller.handleDrop}
        onKeyDown={controller.handleDropZoneKeyDown}
      />
      <UploadBundleSummary assets={dialog.form.assets} />
      <FolderReadGuide failure={dialog.readFailure} folderName={scan?.rootName ?? null} />
    </>
  );

  // 출처 오른쪽 셀. 서버는 complete 단계에서만 미선언을 막는다. 그래서 필수 표시를 붙이고,
  // UploadWindowController.validate 가 시작 전에 막는다.
  const dataOriginCell = (
    <Field
      label={
        <>
          행동 주체 <span className={styles.requiredMark}>*</span>
        </>
      }
      layout="stack"
      disabled={fieldsDisabled}
    >
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.default}
        label="행동 주체"
        fullWidth
        placeholder="선택 안 함"
        options={DATA_ORIGIN_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
          description: option.description,
        }))}
        value={dialog.form.dataOrigin}
        disabled={fieldsDisabled}
        onValueChange={(value) => controller.handleDataOriginChange(value as DataOrigin)}
      />
    </Field>
  );

  // 예약 라벨과 행동 주체 아래의 전체폭 행. 카탈로그에서 바로 고르므로 중간 모달을 두지 않는다.
  // 항목 설명(meta)은 드롭다운 안에 함께 표시된다.
  const embodimentCell = (
    <Field label="Embodiment" layout="stack" disabled={fieldsDisabled}>
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.default}
        label="Embodiment"
        fullWidth
        placeholder="선택 안 함"
        options={embodiments.map((entry) => ({
          value: entry.uuid,
          label: formatEmbodimentName(entry),
          description: formatEmbodimentMeta(entry),
        }))}
        value={embodiment?.uuid ?? null}
        searchable
        searchPlaceholder="Embodiment를 검색해 보세요."
        emptyMessage={embodimentEmptyMessage(embodimentCatalog)}
        disabled={fieldsDisabled}
        onValueChange={(value) =>
          controller.handleEmbodimentChange(
            embodiments.find((entry) => entry.uuid === value) ?? null,
          )
        }
      />
    </Field>
  );

  // 업로드 위치 셀. 위치는 잡을 만들 때 고정되므로 새 업로드에서만 고를 수 있다.
  // 이어받기와 재업로드에서는 서버가 code 를 받지 않는다(reset 422 extra). 잡 응답도 위치를
  // 포함하지 않으므로 표시할 값이 없다.
  // 고를 위치가 하나뿐이거나 카탈로그를 받지 못했으면 컨트롤을 두지 않는다(조직 기본값을 쓴다).
  //
  // 위치마다 카드 하나를 둔다. 모든 위치의 설명이 항상 보인다. 드롭다운은 고르지 않은 위치의 설명을
  // 메뉴 안에 두고 한 줄로 자른다. 그러면 사용자가 위치의 차이를 읽지 않고 시작한다.
  // 화면은 위치를 권장하지 않는다. 기본 위치를 골라 두고, 바꾸는 일은 사용자가 한다.
  const selectedLocation = displayedUploadLocation(uploadLocations, dialog.form.uploadLocationCode);
  const locationField =
    dialog.mode === "new" && uploadLocations.length >= 2 ? (
      <Field
        label={
          <>
            업로드 서버 위치{" "}
            <HatTextTooltip text="업로드한 파일이 먼저 도착하는 서버입니다. 시작한 뒤에는 바꿀 수 없습니다.">
              <span className={styles.locationHelp} aria-label="업로드 서버 위치 안내">
                ⓘ
              </span>
            </HatTextTooltip>
          </>
        }
        layout="stack"
        disabled={fieldsDisabled}
      >
        <div className={styles.locationChoices} role="radiogroup" aria-label="업로드 서버 위치">
          {uploadLocations.map((location) => {
            const selected = selectedLocation?.code === location.code;
            return (
              <label
                key={location.code}
                className={styles.locationChoice}
                data-selected={selected ? "true" : "false"}
                data-disabled={fieldsDisabled ? "true" : "false"}
              >
                <input
                  type="radio"
                  name={locationGroupName}
                  value={location.code}
                  checked={selected}
                  disabled={fieldsDisabled}
                  onChange={() => controller.handleUploadLocationChange(location.code)}
                />
                {/* 이름과 설명은 c6 Text 가 아니라 평문 span 이다. lint 는 컴포넌트 안쪽의 label
                    접근 이름을 검사하지 못한다. 타이포는 CSS 가 같은 토큰으로 맞춘다. */}
                <span className={styles.locationChoiceText}>
                  <span className={styles.locationChoiceLabel}>
                    {location.is_default
                      ? `${location.display_name} (기본)`
                      : location.display_name}
                  </span>
                  {/* 고르는 근거는 서버가 소유한다. 화면이 문장을 따로 만들면 사람과 에이전트가
                      서로 다른 설명을 보게 된다. */}
                  <span className={styles.locationChoiceHint}>{location.description}</span>
                </span>
              </label>
            );
          })}
        </div>
      </Field>
    ) : null;

  return (
    <>
      <form className={styles.dialogForm} onSubmit={controller.handleSubmit}>
        {showResumeBody ? (
          <ResumeBody dialog={dialog} dropZone={dropZone} />
        ) : (
          <>
            {dialog.resumeCandidate !== null && !disabled ? (
              <ResumeCandidateBanner
                candidate={dialog.resumeCandidate}
                onResume={controller.resumeFromCandidate}
              />
            ) : null}
            <div className={styles.fields}>
              {isRetry && !disabled ? (
                <div className={styles.resumeNotice} role="status">
                  <Text variant={hatcheryTextModes["body-bold"]}>업로드를 다시 시작합니다</Text>
                  <Text variant={hatcheryTextModes["caption-normal"]} className={styles.uploadMeta}>
                    {dialog.targetJob?.display_name} ·{" "}
                    {"폴더를 다시 선택하면 처음부터 다시 업로드됩니다."}
                  </Text>
                </div>
              ) : null}
              <Field
                label={
                  <>
                    Dataset 이름 <span className={styles.requiredMark}>*</span>
                  </>
                }
                layout="stack"
                disabled={fieldsDisabled || nameLocked}
              >
                <TextInput
                  value={dialog.form.displayName}
                  maxLength={128}
                  required
                  readOnly={nameLocked}
                  disabled={fieldsDisabled}
                  onChange={controller.handleDisplayNameChange}
                />
              </Field>
              <ReservedLabelForm
                contracts={dataset.reservedContracts}
                keys={dataset.reservedKeys}
                requiredKeys={dataset.reservedRequiredKeys}
                selections={dialog.form.reservedSelections}
                disabled={fieldsDisabled}
                onSelectionsChange={controller.handleReservedSelectionsChange}
                // 환경·출처·행동 주체 세 드롭다운을 균등 3열의 한 행에 둔다. embodiment 는 아래 전체폭 행에 둔다.
                columns={3}
                trailingCell={dataOriginCell}
                extraCell={embodimentCell}
              />
              {/* 밴드 집계는 반입 뒤 에피소드 진단 결과를 모아 기준 밴드와 큐레이션을 계산한다.
                  결과를 쓰지 않는 반입에서는 그 시간이 모두 낭비다. 에피소드 결함 검사는 이
                  값과 무관하게 항상 실행한다. 출처를 open_source 로 고르면 컨트롤러가 자동으로
                  해제한다. 하지만 판정 기준은 이 값뿐이다.

                  제목은 한 줄로 고정한다. 설명은 그 오른쪽에 두 줄로 둔다. 논문 귀속은 여기에
                  표시하지 않는다. 진단 표면의 캡션(DIAGNOSTICS_PAPER_NOTE)이 소유한다. 귀속 범위의
                  SoT: registry BE `docs/specs/dataset-learning-diagnostics.md` §4. */}
              <div className={styles.uploadCheckboxRow}>
                <Checkbox
                  id={bandDiagnosticsToggleId}
                  // 옆 제목 글자와 같은 높이로 맞춘다(기본 1.7em 은 한 줄보다 크다).
                  size="1em"
                  checked={dialog.form.bandDiagnosticsEnabled}
                  disabled={fieldsDisabled || !bandDiagnosticsSupported}
                  onChange={(event) =>
                    controller.handleBandDiagnosticsEnabledChange(event.target.checked)
                  }
                />
                <label htmlFor={bandDiagnosticsToggleId}>
                  <span className={styles.uploadCheckboxTitle}>밴드 진단 산출</span>
                  <Text variant={hatcheryTextModes["caption-normal"]} className={styles.uploadMeta}>
                    {bandDiagnosticsSupported ? (
                      <>
                        에피소드를 모아 기준 밴드를 산출합니다.
                        <br />
                        끄면 밴드 집계를 생략합니다.
                      </>
                    ) : (
                      "이 업로드의 저장 위치는 진단 기능을 지원하지 않습니다."
                    )}
                  </Text>
                </label>
              </div>
              {locationField}
            </div>

            {dropZone}
            {rejectedCount > 0 ? (
              <Text
                variant={hatcheryTextModes["caption-normal"]}
                className={styles.uploadMeta}
                role="status"
              >
                {`${rejectedCount}개 파일은 업로드할 수 없어 제외됩니다(시스템 파일·잘못된 경로·중복).`}
              </Text>
            ) : null}
          </>
        )}

        {dialog.errorMessage ? (
          <Text
            variant={hatcheryTextModes["body-normal"]}
            className={styles.dialogError}
            role="alert"
          >
            {dialog.errorMessage}
          </Text>
        ) : null}

        {disabled ? null : (
          <div className={styles.dialogActions}>
            {isResume ? (
              <>
                <IconButton
                  text="닫기"
                  type="button"
                  variant="surface"
                  disabled={dialog.busy}
                  onClick={onCancel}
                />
                <IconButton
                  text="업로드 취소"
                  type="button"
                  variant="surface"
                  disabled={dialog.busy}
                  onClick={controller.cancelTargetUpload}
                />
                <IconButton
                  text="남은 파일 업로드"
                  type="button"
                  variant="solid"
                  disabled={scan === null || dialog.busy || dialog.scanning || folderBlocked}
                  onClick={controller.resumeUpload}
                />
              </>
            ) : isRetry ? (
              <>
                <IconButton text="취소" type="button" variant="surface" onClick={onCancel} />
                <IconButton
                  text="다시 업로드"
                  type="button"
                  variant="solid"
                  disabled={scan === null || dialog.scanning || folderBlocked}
                  onClick={controller.retryUpload}
                />
              </>
            ) : (
              <>
                <IconButton text="취소" type="button" variant="surface" onClick={onCancel} />
                <IconButton
                  text={submitText}
                  type="submit"
                  variant="solid"
                  disabled={scan === null || dialog.scanning || folderBlocked}
                />
              </>
            )}
          </div>
        )}
      </form>
    </>
  );
}

// new 모드 상단의 미완료 업로드 배너. 다른 브라우저가 유효한 lease 를 점유 중이면 이어받기를
// 막고 사유를 표시한다.
function ResumeCandidateBanner({
  candidate,
  onResume,
}: {
  candidate: NonNullable<UploadDialogState["resumeCandidate"]>;
  onResume: () => void;
}): ReactNode {
  const foreign = isForeignLiveLease(candidate);
  return (
    <div className={styles.resumeNotice} role="status">
      <Text variant={hatcheryTextModes["body-bold"]}>완료되지 않은 업로드가 있습니다</Text>
      <Text variant={hatcheryTextModes["caption-normal"]} className={styles.uploadMeta}>
        {candidate.display_name}
        {foreign ? " · 다른 브라우저에서 업로드 중" : ""}
      </Text>
      <div className={styles.dialogActions}>
        <IconButton
          text="이어서 업로드"
          type="button"
          variant="surface"
          size="sm"
          disabled={foreign}
          onClick={onResume}
        />
      </div>
    </div>
  );
}

// Embodiment 드롭다운의 빈 메뉴 문구. 받기 전과 받지 못한 경우를 "없습니다" 로 알리지 않는다.
// Embodiment 는 지정하지 않아도 올릴 수 있다. 그래서 실패 문구가 다음 경로를 함께 알린다.
function embodimentEmptyMessage(status: EmbodimentCatalogStatus): string {
  if (status === "loading") {
    return "Embodiment 목록을 불러오는 중입니다.";
  }
  if (status === "error") {
    return "Embodiment 목록을 불러오지 못했습니다. 지정하지 않고 올린 뒤 리뷰에서 정할 수 있습니다.";
  }
  return "선택할 embodiment가 없습니다.";
}

// resume 모드 본문. 폴더 재지정 안내와 probe diff 요약을 표시한다.
function ResumeBody({
  dialog,
  dropZone,
}: {
  dialog: UploadDialogState;
  dropZone: ReactNode;
}): ReactNode {
  const diff = dialog.resumeDiff;
  // 행동 주체 필드가 없을 때 만든 잡은 seed Dataset 에 선언이 없다. 그래서 완료할 때 서버가 막는다.
  // 이어받기 payload 에는 이 값을 담을 필드가 없으므로 재업로드로 안내한다.
  const dataOriginMissing = dialog.targetJob?.data_origin === null;
  return (
    <div className={styles.fields}>
      <div className={styles.resumeNotice} role="status">
        <Text variant={hatcheryTextModes["body-bold"]}>
          {dialog.targetJob?.display_name ?? "업로드"}
        </Text>
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.uploadMeta}>
          같은 폴더를 다시 선택하면 이미 올라간 파일은 건너뛰고 남은 파일만 이어서 올립니다.
        </Text>
      </div>
      {dataOriginMissing ? (
        <div className={styles.resumeNotice} role="status">
          <Text variant={hatcheryTextModes["body-bold"]}>행동 주체가 선언되지 않았습니다</Text>
          <Text variant={hatcheryTextModes["caption-normal"]} className={styles.uploadMeta}>
            이어서 올려도 완료 단계에서 막힙니다. 이 업로드를 취소하고 폴더를 다시 선택해 업로드해
            주세요.
          </Text>
        </div>
      ) : null}
      {dropZone}
      {dialog.resumeProbing ? (
        <LoadingState size="sm">서버에 업로드된 파일을 확인하고 있습니다.</LoadingState>
      ) : dialog.resumeDiffFailed ? (
        // 미리보기만 없다. 재개는 막지 않으므로 오류가 아니라 안내로 알린다.
        <EmptyState size="sm">
          서버에 올라간 파일을 확인하지 못했습니다. 이어서 올리기는 그대로 할 수 있고, 시작할 때
          다시 확인합니다.
        </EmptyState>
      ) : diff !== null ? (
        <div className={styles.resumeNotice} role="status">
          <Text variant={hatcheryTextModes["body-normal"]}>
            {`이미 업로드됨 ${diff.skippedCount.toLocaleString()}개 · 남은 ${diff.remainingCount.toLocaleString()}개 (${formatUploadBytes(
              diff.remainingBytes,
            )}) · 예상 ${formatUploadEta(diff.remainingBytes)}`}
          </Text>
          {diff.mismatchedCount > 0 ? (
            <Text variant={hatcheryTextModes["caption-normal"]} className={styles.uploadMeta}>
              {`${diff.mismatchedCount}개 파일이 서버에 저장된 크기와 달라 다시 업로드됩니다.`}
            </Text>
          ) : null}
          {diff.orphanCount > 0 ? (
            <Text variant={hatcheryTextModes["caption-normal"]} className={styles.uploadMeta}>
              {`서버에만 있는 파일 ${diff.orphanCount}개는 업로드 시작 시 삭제됩니다(선택 폴더 기준).`}
            </Text>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
